/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { useQuery } from "@apollo/client";
import { Container, EmptyState, Loading } from "@fluidattacks/design";
import _ from "lodash";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { GET_ORGANIZATION_GROUPS_FINDINGS } from "../queries";
import { SectionHeader } from "components/section-header";
import { Charts } from "features/charts";
import type { IOrganizationAnalyticsProps } from "pages/organization/analytics/types";
import { Logger } from "utils/logger";

const OrganizationAnalytics: React.FC<IOrganizationAnalyticsProps> = ({
  organizationId,
}): JSX.Element => {
  const { t } = useTranslation();

  const searchParams = new URLSearchParams(useLocation().search);
  const maybeOrganizationId = searchParams.get("organization");

  /*
   * Attempt to read the organization ID from passed Components properties,
   *   or from URL search query, whatever is available first
   */
  const auxOrganizationId = _.isUndefined(organizationId) ? "" : organizationId;
  const subject = _.isNull(maybeOrganizationId)
    ? auxOrganizationId
    : maybeOrganizationId;
  const paddingLarge = 1.25;

  const { data, loading } = useQuery(GET_ORGANIZATION_GROUPS_FINDINGS, {
    fetchPolicy: "cache-first",
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.warning("An error occurred loading group data", error);
      });
    },
    variables: {
      organizationId: subject,
    },
  });

  if (loading) {
    return (
      <Container
        alignItems={"center"}
        display={"flex"}
        justify={"center"}
        left={"50%"}
        position={"absolute"}
        top={"70%"}
      >
        <Loading size={"lg"} />
      </Container>
    );
  }
  const groupsData = data?.organization.groups;

  const noGroupHasFindings = groupsData?.every(
    (group): boolean => group.findings !== null && group.findings.length === 0,
  );

  const reportMode = searchParams.get("reportMode") === "true";

  if (!reportMode && noGroupHasFindings) {
    return (
      <React.Fragment>
        <SectionHeader header={t("analytics.header")} />
        <Container
          alignItems={"center"}
          display={"flex"}
          gap={0.5}
          justify={"end"}
          px={paddingLarge}
          py={paddingLarge}
        >
          <EmptyState
            description={t("analytics.emptyChart.description")}
            imageSrc={"integrates/empty/analyticsIcon"}
            padding={0}
            title={t("analytics.emptyChart.title")}
          />
        </Container>
      </React.Fragment>
    );
  }

  return (
    <React.StrictMode>
      <Charts
        bgChange={searchParams.get("bgChange") === "true"}
        entity={"organization"}
        reportMode={reportMode}
        subject={subject}
      />
    </React.StrictMode>
  );
};

export { OrganizationAnalytics };
